import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUnits } from '@/contexts/UnitsContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Settings, Globe, User, Mail, MessageCircle, Loader2, Send, ExternalLink, Server, Gauge, FileText, LogIn, LogOut as LogOutIcon, Smartphone, Clock, Bell, AlertTriangle, Home, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WhatsAppSettingsCard from '@/components/whatsapp/WhatsAppSettingsCard';
import SystemMonitorsCard from '@/components/monitoring/SystemMonitorsCard';
import BriefingsCard from '@/components/monitoring/BriefingsCard';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import { getHomeOptions } from '@/lib/navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUserLanguage, updateUserHomePage, isAdmin, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    timezone,
    timezoneLabel,
    timezoneOptions,
    saveSettings: saveTimezoneSettings,
    formatDateTime,
  } = useTimezone();
  const {
    speedUnit,
    distanceUnit,
    temperatureUnit,
    pressureUnit,
    depthUnit,
    volumeUnit,
    flowUnit,
    runtimeUnit,
    speedOptions,
    distanceOptions,
    temperatureOptions,
    pressureOptions,
    depthOptions,
    volumeOptions,
    flowOptions,
    runtimeOptions,
    saveSettings: saveUnitsSettings,
  } = useUnits();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingUnits, setSavingUnits] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSystemEmail, setTestingSystemEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // Access logs state
  const [accessLogs, setAccessLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Local units state
  const [localSpeedUnit, setLocalSpeedUnit] = useState(speedUnit);
  const [localDistanceUnit, setLocalDistanceUnit] = useState(distanceUnit);
  const [localTemperatureUnit, setLocalTemperatureUnit] = useState(temperatureUnit);
  const [localPressureUnit, setLocalPressureUnit] = useState(pressureUnit);
  const [localDepthUnit, setLocalDepthUnit] = useState(depthUnit);
  const [localVolumeUnit, setLocalVolumeUnit] = useState(volumeUnit);
  const [localFlowUnit, setLocalFlowUnit] = useState(flowUnit);
  const [localRuntimeUnit, setLocalRuntimeUnit] = useState(runtimeUnit);
  const [localTimezone, setLocalTimezone] = useState(timezone);
  
  // Telemetry settings state (v3.4)
  const [telemetrySettings, setTelemetrySettings] = useState({
    sync_interval_ms: 1000
  });
  const [savingTelemetry, setSavingTelemetry] = useState(false);
  
  // Version info
  const [versionInfo, setVersionInfo] = useState({ version: '...', build_date: '', changelog: [] });

  // System email settings (for password recovery)
  const [emailSettings, setEmailSettings] = useState({
    gmail_user: '',
    gmail_app_password: '',
    enabled: false,
    has_password: false,
  });

  // MQTT Server settings
  const [mqttSettings, setMqttSettings] = useState({
    broker_host: '',
    broker_port: 1883,
    admin_user: '',
    admin_password: '',
    use_tls: false,
    has_password: false,
  });
  const [testingMqtt, setTestingMqtt] = useState(false);

  // Evolution/WhatsApp settings
  const [evolutionSettings, setEvolutionSettings] = useState({
    base_url: '',
    instance: '',
    api_key: '',
    is_enabled: false,
    api_key_set: false,
  });
  const [savingEvolution, setSavingEvolution] = useState(false);
  const [testingEvolution, setTestingEvolution] = useState(false);
  const [testingEvolutionMessage, setTestingEvolutionMessage] = useState(false);
  const [evolutionTestPhone, setEvolutionTestPhone] = useState('');
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'account');

  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_email: '',
    smtp_password: '',
  });

  const fetchSettings = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API}/settings/notifications`),
        axios.get(`${API}/settings/email`),
        axios.get(`${API}/settings/mqtt`),
        axios.get(`${API}/settings/telemetry`)
      ];
      
      // Buscar Evolution apenas se for admin
      if (isAdmin) {
        requests.push(axios.get(`${API}/settings/evolution`).catch(() => ({ data: {} })));
      }
      
      const [notifRes, emailRes, mqttRes, telemetryRes, evolutionRes] = await Promise.all(requests);
      
      setNotificationSettings(prev => ({
        ...prev,
        ...notifRes.data,
        smtp_password: '',
      }));
      
      setEmailSettings({
        gmail_user: emailRes.data.gmail_user || '',
        gmail_app_password: '',
        enabled: emailRes.data.enabled || false,
        has_password: emailRes.data.has_password || false,
      });

      setMqttSettings({
        broker_host: mqttRes.data.broker_host || '',
        broker_port: mqttRes.data.broker_port || 1883,
        admin_user: mqttRes.data.admin_user || '',
        admin_password: '',
        use_tls: mqttRes.data.use_tls || false,
        has_password: mqttRes.data.has_password || false,
      });
      
      setTelemetrySettings({
        sync_interval_ms: telemetryRes.data.sync_interval_ms || 1000
      });
      
      if (evolutionRes?.data) {
        setEvolutionSettings({
          base_url: evolutionRes.data.base_url || '',
          instance: evolutionRes.data.instance || '',
          api_key: '',
          is_enabled: evolutionRes.data.is_enabled || false,
          api_key_set: evolutionRes.data.api_key_set || false,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Fetch version info
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await axios.get(`${API}/version`);
        setVersionInfo(res.data);
      } catch (error) {
        console.error('Error fetching version:', error);
      }
    };
    fetchVersion();
  }, []);

  // Fetch access logs (admin only)
  const fetchAccessLogs = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/access-logs?limit=100`);
      setAccessLogs(res.data);
    } catch (error) {
      console.error('Error fetching access logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  }, [isAdmin]);

  // Sync local units with context
  useEffect(() => {
    setLocalSpeedUnit(speedUnit);
    setLocalDistanceUnit(distanceUnit);
    setLocalTemperatureUnit(temperatureUnit);
    setLocalPressureUnit(pressureUnit);
    setLocalDepthUnit(depthUnit);
    setLocalVolumeUnit(volumeUnit);
    setLocalFlowUnit(flowUnit);
    setLocalRuntimeUnit(runtimeUnit);
  }, [
    speedUnit,
    distanceUnit,
    temperatureUnit,
    pressureUnit,
    depthUnit,
    volumeUnit,
    flowUnit,
    runtimeUnit,
  ]);

  useEffect(() => {
    setLocalTimezone(timezone);
  }, [timezone]);

  const handleLanguageChange = async (language) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { language });
      i18n.changeLanguage(language);
      updateUserLanguage(language);
      localStorage.setItem('i18nextLng', language);
      toast.success(t('saveSuccess'));
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const homeOptions = getHomeOptions({ isAdmin, hasPermission });

  const handleHomePageChange = async (homePage) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { home_page: homePage });
      updateUserHomePage(homePage);
      toast.success('Tela inicial atualizada.');
    } catch (error) {
      console.error('Error updating home page:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, notificationSettings);
      toast.success('Configurações de notificação salvas');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    try {
      const success = await saveTimezoneSettings(localTimezone);
      if (success) {
        toast.success('Timezone padrão do sistema atualizada');
      } else {
        toast.error('Erro ao salvar timezone do sistema');
      }
    } catch (error) {
      console.error('Error saving timezone settings:', error);
      toast.error('Erro ao salvar timezone do sistema');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, {
        gmail_user: emailSettings.gmail_user,
        gmail_app_password: emailSettings.gmail_app_password || undefined,
        enabled: emailSettings.enabled,
      });
      setEmailSettings(prev => ({ ...prev, gmail_app_password: '', has_password: true }));
      toast.success('Configurações de email do sistema salvas');
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error('Erro ao salvar configurações de email');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSystemEmail = async () => {
    setTestingSystemEmail(true);
    try {
      await axios.post(`${API}/settings/email/test`);
      toast.success('Email de teste enviado para seu email cadastrado!');
    } catch (error) {
      console.error('Error testing system email:', error);
      toast.error(error.response?.data?.detail || 'Falha ao enviar email de teste');
    } finally {
      setTestingSystemEmail(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Informe um email para teste');
      return;
    }
    setTestingEmail(true);
    try {
      await axios.post(`${API}/settings/notifications/test-email?email=${encodeURIComponent(testEmail)}`);
      toast.success('Email de teste enviado!');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Falha ao enviar email de teste. Verifique as configurações.');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveMqttSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/mqtt`, {
        broker_host: mqttSettings.broker_host,
        broker_port: mqttSettings.broker_port,
        admin_user: mqttSettings.admin_user,
        admin_password: mqttSettings.admin_password || undefined,
        use_tls: mqttSettings.use_tls,
      });
      setMqttSettings(prev => ({ ...prev, admin_password: '', has_password: true }));
      toast.success(t('saveSuccess'));
    } catch (error) {
      console.error('Error saving MQTT settings:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestMqtt = async () => {
    setTestingMqtt(true);
    try {
      const res = await axios.post(`${API}/settings/mqtt/test`);
      if (res.data.success) {
        toast.success(t('mqttConnectionSuccess'));
      } else {
        toast.error(res.data.error || t('mqttConnectionFailed'));
      }
    } catch (error) {
      console.error('Error testing MQTT:', error);
      toast.error(error.response?.data?.detail || t('mqttConnectionFailed'));
    } finally {
      setTestingMqtt(false);
    }
  };

  const handleSaveEvolution = async () => {
    setSavingEvolution(true);
    try {
      const payload = {
        base_url: evolutionSettings.base_url,
        instance: evolutionSettings.instance,
        is_enabled: evolutionSettings.is_enabled,
      };
      
      if (evolutionSettings.api_key) {
        payload.api_key = evolutionSettings.api_key;
      }
      
      await axios.put(`${API}/settings/evolution`, payload);
      toast.success('Configurações da Evolution API salvas');
      
      setEvolutionSettings(prev => ({
        ...prev,
        api_key: '',
        api_key_set: prev.api_key_set || Boolean(prev.api_key),
      }));
    } catch (error) {
      console.error('Error saving Evolution settings:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações da Evolution API');
    } finally {
      setSavingEvolution(false);
    }
  };

  const handleTestEvolution = async () => {
    setTestingEvolution(true);
    try {
      const res = await axios.post(`${API}/settings/evolution/test`);
      if (res.data.success) {
        toast.success(`Conexão OK! Instância: ${res.data.instance || evolutionSettings.instance}`);
      } else {
        toast.error(res.data.error || 'Falha na conexão com Evolution API');
      }
    } catch (error) {
      console.error('Error testing Evolution:', error);
      toast.error(error.response?.data?.detail || 'Erro ao testar conexão');
    } finally {
      setTestingEvolution(false);
    }
  };

  const handleTestEvolutionMessage = async () => {
    if (!evolutionTestPhone) {
      toast.error('Informe um telefone para teste');
      return;
    }
    setTestingEvolutionMessage(true);
    try {
      await axios.post(`${API}/settings/evolution/test-message?phone=${encodeURIComponent(evolutionTestPhone)}`);
      toast.success('Mensagem WhatsApp de teste enviada');
    } catch (error) {
      console.error('Error testing Evolution message:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem de teste');
    } finally {
      setTestingEvolutionMessage(false);
    }
  };

  const handleSaveUnits = async () => {
    setSavingUnits(true);
    try {
      const success = await saveUnitsSettings({
        speed_unit: localSpeedUnit,
        distance_unit: localDistanceUnit,
        temperature_unit: localTemperatureUnit,
        pressure_unit: localPressureUnit,
        depth_unit: localDepthUnit,
        volume_unit: localVolumeUnit,
        flow_unit: localFlowUnit,
        runtime_unit: localRuntimeUnit,
      });
      if (success) {
        toast.success('Configurações de unidades salvas');
      } else {
        toast.error('Erro ao salvar configurações de unidades');
      }
    } catch (error) {
      console.error('Error saving units:', error);
      toast.error('Erro ao salvar configurações de unidades');
    } finally {
      setSavingUnits(false);
    }
  };

  // Salvar configurações de telemetria
  const handleSaveTelemetry = async () => {
    setSavingTelemetry(true);
    try {
      await axios.put(`${API}/settings/telemetry`, telemetrySettings);
      toast.success('Configurações de telemetria salvas. Reinicie o agente para aplicar.');
    } catch (error) {
      console.error('Error saving telemetry settings:', error);
      toast.error('Erro ao salvar configurações de telemetria');
    } finally {
      setSavingTelemetry(false);
    }
  };

  return (
    <div className="page-container space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings')}</h1>
        <p className="text-muted-foreground">Gerencie suas preferências e notificações</p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          const next = new URLSearchParams(searchParams);
          if (value === 'account') next.delete('tab');
          else next.set('tab', value);
          setSearchParams(next, { replace: true });
          if (value === 'logs') {
            fetchAccessLogs();
          }
        }}
      >
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="account" className="gap-2 flex-shrink-0">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">{t('settings')}</span>
          </TabsTrigger>
          <TabsTrigger value="units" className="gap-2 flex-shrink-0">
            <Gauge className="w-4 h-4" />
            <span className="hidden sm:inline">Unidades</span>
          </TabsTrigger>
          <TabsTrigger value="telemetry" className="gap-2 flex-shrink-0">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Telemetria</span>
          </TabsTrigger>
          <TabsTrigger value="mqtt" className="gap-2 flex-shrink-0">
            <Server className="w-4 h-4" />
            <span className="hidden sm:inline">MQTT</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2 flex-shrink-0">
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2 flex-shrink-0">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Alertas</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="whatsapp" className="gap-2 flex-shrink-0">
              <Smartphone className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="monitoring" className="gap-2 flex-shrink-0">
              <Activity className="w-4 h-4" />
              <span>Monitoramento</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="logs" className="gap-2 flex-shrink-0">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Logs</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="about" className="gap-2 flex-shrink-0">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Info</span>
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <CardTitle>Conta</CardTitle>
              </div>
              <CardDescription>Informações da sua conta</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-sm">{t('fullName')}</Label>
                  <p className="font-medium">{user?.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">{t('username')}</Label>
                  <p className="font-medium font-mono">{user?.username}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                <CardTitle>{t('language')}</CardTitle>
              </div>
              <CardDescription>Escolha o idioma da interface</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>{t('language')}</Label>
                <Select
                  value={user?.language || 'pt'}
                  onValueChange={handleLanguageChange}
                >
                  <SelectTrigger className="w-full max-w-xs" data-testid="settings-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt">
                      <div className="flex items-center gap-2">🇧🇷 {t('portuguese')}</div>
                    </SelectItem>
                    <SelectItem value="en">
                      <div className="flex items-center gap-2">🇺🇸 {t('english')}</div>
                    </SelectItem>
                    <SelectItem value="es">
                      <div className="flex items-center gap-2">🇪🇸 {t('spanish')}</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-primary" />
                <CardTitle>Minha tela inicial</CardTitle>
              </div>
              <CardDescription>
                Escolha a tela exibida logo após o login. Sem escolha, você entra pelo Dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Tela ao entrar</Label>
                <Select
                  value={user?.home_page || '/'}
                  onValueChange={handleHomePageChange}
                >
                  <SelectTrigger className="w-full max-w-xs" data-testid="settings-home-page-select">
                    <SelectValue placeholder="Dashboard (padrão)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="/">Dashboard (padrão)</SelectItem>
                    {homeOptions
                      .filter((option) => option.path !== '/')
                      .map((option) => (
                        <SelectItem key={option.path} value={option.path}>
                          {option.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <CardTitle>Timezone do Sistema</CardTitle>
              </div>
              <CardDescription>
                Define o fuso horário padrão usado para exibição de data/hora em todo o Octopus.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Timezone Padrão</Label>
                <Select value={localTimezone} onValueChange={setLocalTimezone}>
                  <SelectTrigger className="w-full max-w-xs" data-testid="settings-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezoneOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Timezone atual: {timezoneLabel} ({timezone})
                </p>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  onClick={handleSaveTimezone}
                  disabled={savingTimezone || !localTimezone}
                  data-testid="save-timezone-btn"
                >
                  {savingTimezone && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar Timezone
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Integration */}
          <WhatsAppSettingsCard user={user} onUpdate={() => window.location.reload()} />
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Gauge className="w-5 h-5 text-primary" />
                <CardTitle>Unidades de Medida</CardTitle>
              </div>
              <CardDescription>
                Configure as unidades de medida usadas em todo o sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Velocidade</Label>
                  <Select value={localSpeedUnit} onValueChange={setLocalSpeedUnit}>
                    <SelectTrigger data-testid="speed-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {speedOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em KPIs e grafico de velocidade.</p>
                </div>

                <div className="space-y-2">
                  <Label>Distancia</Label>
                  <Select value={localDistanceUnit} onValueChange={setLocalDistanceUnit}>
                    <SelectTrigger data-testid="distance-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {distanceOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em distancia total e trajetos.</p>
                </div>

                <div className="space-y-2">
                  <Label>Temperatura</Label>
                  <Select value={localTemperatureUnit} onValueChange={setLocalTemperatureUnit}>
                    <SelectTrigger data-testid="temperature-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {temperatureOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em graficos de temperatura.</p>
                </div>

                <div className="space-y-2">
                  <Label>Pressao</Label>
                  <Select value={localPressureUnit} onValueChange={setLocalPressureUnit}>
                    <SelectTrigger data-testid="pressure-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pressureOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em cards de pressao na monitoracao.</p>
                </div>

                <div className="space-y-2">
                  <Label>Profundidade</Label>
                  <Select value={localDepthUnit} onValueChange={setLocalDepthUnit}>
                    <SelectTrigger data-testid="depth-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {depthOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em fontes de navegacao e sonar.</p>
                </div>

                <div className="space-y-2">
                  <Label>Volume</Label>
                  <Select value={localVolumeUnit} onValueChange={setLocalVolumeUnit}>
                    <SelectTrigger data-testid="volume-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {volumeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em KPI de consumo total.</p>
                </div>

                <div className="space-y-2">
                  <Label>Vazao</Label>
                  <Select value={localFlowUnit} onValueChange={setLocalFlowUnit}>
                    <SelectTrigger data-testid="flow-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {flowOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em graficos de consumo instantaneo.</p>
                </div>

                <div className="space-y-2">
                  <Label>Tempo de operacao</Label>
                  <Select value={localRuntimeUnit} onValueChange={setLocalRuntimeUnit}>
                    <SelectTrigger data-testid="runtime-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {runtimeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label} ({opt.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Aplicado em horas de motor e operacao.</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-center gap-4">
                <Button 
                  onClick={handleSaveUnits} 
                  disabled={savingUnits}
                  data-testid="save-units-btn"
                >
                  {savingUnits && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar Unidades
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Telemetry Tab */}
        <TabsContent value="telemetry" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <CardTitle>Configurações de Telemetria</CardTitle>
              </div>
              <CardDescription>
                Configure o intervalo de agregação dos dados de telemetria
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Agregação Síncrona de Dados</h4>
                <p className="text-sm text-muted-foreground">
                  O sistema coleta dados de múltiplas fontes (GPS, motores, sensores) que reportam em frequências diferentes.
                  O intervalo de agregação define quando todos os dados são consolidados com um timestamp único,
                  facilitando a correlação e análise.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Intervalo de Agregação</Label>
                  <Select
                    value={String(telemetrySettings.sync_interval_ms)}
                    onValueChange={(value) => setTelemetrySettings(prev => ({
                      ...prev,
                      sync_interval_ms: parseInt(value)
                    }))}
                  >
                    <SelectTrigger className="w-full max-w-xs" data-testid="telemetry-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="500">
                        <div className="flex items-center justify-between gap-4">
                          <span>Alta Frequência</span>
                          <span className="text-muted-foreground">(500ms)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="1000">
                        <div className="flex items-center justify-between gap-4">
                          <span>Padrão</span>
                          <span className="text-muted-foreground">(1000ms)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="2000">
                        <div className="flex items-center justify-between gap-4">
                          <span>Economia</span>
                          <span className="text-muted-foreground">(2000ms)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="5000">
                        <div className="flex items-center justify-between gap-4">
                          <span>Baixa Frequência</span>
                          <span className="text-muted-foreground">(5000ms)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Intervalos menores = mais precisão, mais dados. Intervalos maiores = economia de armazenamento.
                  </p>
                </div>

                <Separator />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="text-lg font-bold">
                      {telemetrySettings.sync_interval_ms === 500 ? '7.200' :
                       telemetrySettings.sync_interval_ms === 1000 ? '3.600' :
                       telemetrySettings.sync_interval_ms === 2000 ? '1.800' : '720'}
                    </div>
                    <div className="text-xs text-muted-foreground">registros/hora</div>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="text-lg font-bold">
                      {telemetrySettings.sync_interval_ms === 500 ? '~173k' :
                       telemetrySettings.sync_interval_ms === 1000 ? '~86k' :
                       telemetrySettings.sync_interval_ms === 2000 ? '~43k' : '~17k'}
                    </div>
                    <div className="text-xs text-muted-foreground">registros/dia</div>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="text-lg font-bold">{telemetrySettings.sync_interval_ms}ms</div>
                    <div className="text-xs text-muted-foreground">intervalo atual</div>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="text-lg font-bold">
                      {telemetrySettings.sync_interval_ms === 500 ? '+4.5%' :
                       telemetrySettings.sync_interval_ms === 1000 ? '-48%' :
                       telemetrySettings.sync_interval_ms === 2000 ? '-74%' : '-90%'}
                    </div>
                    <div className="text-xs text-muted-foreground">vs. original</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <Button 
                  onClick={handleSaveTelemetry} 
                  disabled={savingTelemetry}
                  data-testid="save-telemetry-btn"
                >
                  {savingTelemetry && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar Configuração
                </Button>
                <p className="text-xs text-muted-foreground">
                  * Reinicie o agente no dispositivo para aplicar as alterações
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MQTT Server Tab */}
        <TabsContent value="mqtt" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-primary" />
                <CardTitle>{t('mqttServerSettings')}</CardTitle>
              </div>
              <CardDescription>
                {t('mqttServerDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Servidor MQTT Configurado:</h4>
                <p className="text-sm text-muted-foreground">
                  O servidor MQTT deve ser um broker Mosquitto rodando em uma VPS separada. 
                  As credenciais configuradas aqui serão usadas pelo Octopus para se conectar ao broker.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('mqttBrokerHost')}</Label>
                  <Input
                    value={mqttSettings.broker_host}
                    onChange={(e) => setMqttSettings({ ...mqttSettings, broker_host: e.target.value })}
                    placeholder="mqtt.seuservidor.com"
                    data-testid="mqtt-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('mqttBrokerPort')}</Label>
                  <Input
                    type="number"
                    value={mqttSettings.broker_port}
                    onChange={(e) => setMqttSettings({ ...mqttSettings, broker_port: parseInt(e.target.value) || 1883 })}
                    placeholder="1883"
                    data-testid="mqtt-port-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('mqttAdminUser')}</Label>
                  <Input
                    value={mqttSettings.admin_user}
                    onChange={(e) => setMqttSettings({ ...mqttSettings, admin_user: e.target.value })}
                    placeholder="octopus_admin"
                    data-testid="mqtt-user-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t('mqttAdminPassword')}
                    {mqttSettings.has_password && (
                      <span className="text-xs text-muted-foreground ml-2">(configurado)</span>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={mqttSettings.admin_password}
                    onChange={(e) => setMqttSettings({ ...mqttSettings, admin_password: e.target.value })}
                    placeholder={mqttSettings.has_password ? '••••••••••••••••' : 'senha_do_broker'}
                    data-testid="mqtt-password-input"
                  />
                  {mqttSettings.has_password && (
                    <p className="text-xs text-muted-foreground">Deixe em branco para manter a senha atual</p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="mqtt-tls"
                  checked={mqttSettings.use_tls}
                  onCheckedChange={(checked) => setMqttSettings({ ...mqttSettings, use_tls: checked })}
                  data-testid="mqtt-tls-switch"
                />
                <Label htmlFor="mqtt-tls">{t('mqttUseTls')}</Label>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <Button 
                  onClick={handleSaveMqttSettings} 
                  disabled={saving}
                  data-testid="save-mqtt-settings-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t('save')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTestMqtt}
                  disabled={testingMqtt || !mqttSettings.broker_host}
                  data-testid="test-mqtt-btn"
                >
                  {testingMqtt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ml-2">{t('mqttTestConnection')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Email Tab (for password recovery) */}
        <TabsContent value="email" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <CardTitle>Email do Sistema</CardTitle>
                </div>
                <Switch
                  checked={emailSettings.enabled}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, enabled: checked })}
                  data-testid="system-email-enabled-switch"
                />
              </div>
              <CardDescription>
                Configure o Gmail para envio de emails do sistema (recuperação de senha, notificações do sistema).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Como criar uma Senha de App no Gmail:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Acesse <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Segurança da Conta Google <ExternalLink className="w-3 h-3 inline" /></a></li>
                  <li>Ative a verificação em duas etapas (se ainda não estiver)</li>
                  <li>Vá em &quot;Senhas de app&quot; (ou acesse <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">diretamente <ExternalLink className="w-3 h-3 inline" /></a>)</li>
                  <li>Crie uma nova senha de app para &quot;Octopus&quot;</li>
                  <li>Copie a senha gerada (16 caracteres) e cole abaixo</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email do Gmail</Label>
                  <Input
                    type="email"
                    value={emailSettings.gmail_user}
                    onChange={(e) => setEmailSettings({ ...emailSettings, gmail_user: e.target.value })}
                    placeholder="octopus@empresa.com"
                    data-testid="system-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    App Password 
                    {emailSettings.has_password && (
                      <span className="text-xs text-muted-foreground ml-2">(configurado)</span>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={emailSettings.gmail_app_password}
                    onChange={(e) => setEmailSettings({ ...emailSettings, gmail_app_password: e.target.value })}
                    placeholder={emailSettings.has_password ? '••••••••••••••••' : 'xxxx xxxx xxxx xxxx'}
                    data-testid="system-email-password-input"
                  />
                  {emailSettings.has_password && (
                    <p className="text-xs text-muted-foreground">Deixe em branco para manter a senha atual</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <Button 
                  onClick={handleSaveEmailSettings} 
                  disabled={saving}
                  data-testid="save-email-settings-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTestSystemEmail}
                  disabled={testingSystemEmail || !emailSettings.enabled}
                  data-testid="test-system-email-btn"
                >
                  {testingSystemEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ml-2">Enviar Teste</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-500" />
                <CardTitle>Configuração de Alertas</CardTitle>
              </div>
              <CardDescription>
                Gerencie os canais de notificação para alertas de equipamentos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email Status */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Notificações por Email</p>
                    <p className="text-sm text-muted-foreground">
                      {emailSettings.enabled && emailSettings.gmail_user 
                        ? `Configurado: ${emailSettings.gmail_user}` 
                        : 'Não configurado'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={emailSettings.enabled ? 'default' : 'secondary'}>
                    {emailSettings.enabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setActiveTab('email')}
                  >
                    Configurar
                  </Button>
                </div>
              </div>

              {/* WhatsApp Status */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium">Notificações por WhatsApp</p>
                    <p className="text-sm text-muted-foreground">
                      {evolutionSettings.is_enabled && evolutionSettings.instance
                        ? `Evolution: ${evolutionSettings.instance}`
                        : 'Não configurado'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={evolutionSettings.is_enabled ? 'default' : 'secondary'}>
                    {evolutionSettings.is_enabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setActiveTab('whatsapp')}
                    >
                      Configurar
                    </Button>
                  )}
                </div>
              </div>

              <Separator />

              <div className="bg-secondary/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Como funcionam os alertas
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Os alertas são configurados por equipamento na página de endereçamento de sensores</li>
                  <li>Quando um valor sai da faixa "verde", o sistema gera um alerta</li>
                  <li>O alerta é enviado pelos canais habilitados acima (Email e/ou WhatsApp)</li>
                  <li>Configure os destinatários em cada equipamento</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monitoramento — watchdogs de sistema: alertas que não pertencem a um
            equipamento (os de equipamento ficam em Equipamentos > equipamento >
            Alertas, Fase 1000). Admin apenas: expõe destinatários e credencial de bot. */}
        {isAdmin && (
          <TabsContent value="monitoring" className="space-y-4 mt-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold tracking-tight">Boletins Operacionais</h2>
              <p className="text-sm text-muted-foreground">
                Retrato do período fechado, enviado no horário configurado.
                O diário cobre o dia anterior (00:00–23:59).
              </p>
            </div>
            {/* Boletins (Fase 1001): periódicos, empurrados, sem limiar nem severidade.
                Transversais à frota como os watchdogs — por isso a mesma aba, e
                não CADASTROS nem a página de um equipamento (INV-IA-MENU-CATALOG-001).
                Vêm ANTES dos watchdogs: a seção existia e passava despercebida. */}
            <BriefingsCard />
            <SystemMonitorsCard />
          </TabsContent>
        )}

        {/* WhatsApp/Evolution Settings Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="whatsapp" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-green-500" />
                  <div>
                    <CardTitle>Configurações do WhatsApp (Evolution API)</CardTitle>
                    <CardDescription>
                      Configure a integração com WhatsApp via Evolution API para alertas do Octopus
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="evolution-enabled">Integração Ativa</Label>
                  </div>
                  <Switch
                    id="evolution-enabled"
                    checked={evolutionSettings.is_enabled}
                    onCheckedChange={(checked) => setEvolutionSettings(prev => ({ ...prev, is_enabled: checked }))}
                  />
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="evolution-url">URL da Evolution API</Label>
                    <Input
                      id="evolution-url"
                      value={evolutionSettings.base_url}
                      onChange={(e) => setEvolutionSettings(prev => ({ ...prev, base_url: e.target.value }))}
                      placeholder="http://2.24.201.207:32768"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="evolution-instance">Instância</Label>
                    <Input
                      id="evolution-instance"
                      value={evolutionSettings.instance}
                      onChange={(e) => setEvolutionSettings(prev => ({ ...prev, instance: e.target.value }))}
                      placeholder="octopus-whatsapp"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="evolution-key">
                      API Key {evolutionSettings.api_key_set && <Badge variant="outline" className="ml-2 text-xs">Configurada</Badge>}
                    </Label>
                    <Input
                      id="evolution-key"
                      type="password"
                      value={evolutionSettings.api_key}
                      onChange={(e) => setEvolutionSettings(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder={evolutionSettings.api_key_set ? '••••••••••••••••' : 'Chave da Evolution API'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número para teste</Label>
                    <PhoneNumberInput
                      value={evolutionTestPhone}
                      onChange={setEvolutionTestPhone}
                      idPrefix="evolution-test-phone"
                    />
                    <p className="text-xs text-muted-foreground">
                      O telefone será enviado ao WhatsApp no formato internacional.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleTestEvolution}
                    disabled={testingEvolution || !evolutionSettings.base_url}
                  >
                    {testingEvolution && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Testar Conexão
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleTestEvolutionMessage}
                    disabled={testingEvolutionMessage || !evolutionTestPhone}
                  >
                    {testingEvolutionMessage && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Enviar Teste
                  </Button>
                  <Button
                    onClick={handleSaveEvolution}
                    disabled={savingEvolution}
                  >
                    {savingEvolution && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Salvar Configurações
                  </Button>
                </div>

                <div className="p-4 bg-secondary/50 rounded-lg text-sm">
                  <p className="font-medium mb-2">Como configurar:</p>
                  <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                    <li>Informe a URL pública da Evolution API.</li>
                    <li>Informe a instância conectada ao WhatsApp.</li>
                    <li>Informe a API Key configurada no container da Evolution.</li>
                    <li>Use “Testar Conexão” para validar a instância e “Enviar Teste” para validar envio real.</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Access Logs Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="logs" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <CardTitle>Logs de Acesso</CardTitle>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchAccessLogs}
                    disabled={loadingLogs}
                  >
                    {loadingLogs && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Atualizar
                  </Button>
                </div>
                <CardDescription>
                  Histórico de login e logout de todos os usuários do sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingLogs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : accessLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum registro de acesso encontrado
                  </p>
                ) : (
                  <div className="rounded-md border max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ação</TableHead>
                          <TableHead>Usuário</TableHead>
                          <TableHead>CPF</TableHead>
                          <TableHead>IP</TableHead>
                          <TableHead>Data/Hora</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accessLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <Badge variant={log.action === 'login' ? 'default' : 'secondary'}>
                                {log.action === 'login' ? (
                                  <><LogIn className="w-3 h-3 mr-1" /> Login</>
                                ) : (
                                  <><LogOutIcon className="w-3 h-3 mr-1" /> Logout</>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{log.full_name}</TableCell>
                            <TableCell className="font-mono text-xs">{log.username}</TableCell>
                            <TableCell className="font-mono text-xs">{log.ip_address}</TableCell>
                            <TableCell className="text-sm">
                              {formatDateTime(log.timestamp)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* About Tab */}
        <TabsContent value="about" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                <CardTitle>Sobre</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Versão</Label>
                  <p className="font-medium text-lg">Octopus v{versionInfo.version}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Data do Build</Label>
                  <p className="font-medium">
                    {versionInfo.build_date ? formatDateTime(versionInfo.build_date, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      omitSeconds: true,
                    }) : '--'}
                  </p>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-muted-foreground text-sm">Changelog</Label>
                <ul className="mt-2 space-y-1 text-sm">
                  {versionInfo.changelog?.map((item, idx) => (
                    <li key={idx} className="text-muted-foreground">{item}</li>
                  ))}
                </ul>
              </div>
              <Separator />
              <p className="text-sm text-muted-foreground">
                Painel centralizado para monitoramento de equipamentos IoT industriais via MQTT.
                Sistema de alertas com notificações por email e WhatsApp.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
